import { 
  getFirestore, collection, doc, setDoc, getDocs, updateDoc, deleteDoc, 
  onSnapshot, query, writeBatch 
} from "firebase/firestore";
import { db } from "../firebase";
import { 
  Product, Ingredient, MenuConfig, CategoryConfig 
} from "../types";
import { getTenantId } from "./tenantService";

const cleanUndefined = (obj: any): any => {
    if (obj === undefined || obj === null) return {};
    return JSON.parse(JSON.stringify(obj));
};

export const getProductsByTenant = async (tenantId: string): Promise<Product[]> => {
    try {
        const id = tenantId || 'root';
        const productsCol = collection(db, id === 'root' ? 'products' : `tenants/${id}/products`);
        const snapshot = await getDocs(productsCol);
        return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) } as Product));
    } catch(e) {
        return [];
    }
};

export const addProduct = async (product: Product) => {
    const tenantId = getTenantId() || 'root';
    const productsCol = collection(db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`);
    const newDoc = doc(productsCol);
    
    const prodToSave: any = { ...product };
    if (!prodToSave.createdAt) prodToSave.createdAt = Date.now();
    if (!prodToSave.updatedAt) prodToSave.updatedAt = Date.now();
    
    await setDoc(newDoc, cleanUndefined(prodToSave));
};

export const updateProduct = async (product: Product) => {
    const tenantId = getTenantId() || 'root';
    const productsCol = collection(db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`);
    const prodToSave = { ...product, updatedAt: Date.now() };
    await updateDoc(doc(productsCol, product.id), cleanUndefined(prodToSave));
};

export const deleteProduct = async (id: string) => {
    const tenantId = getTenantId() || 'root';
    const productsCol = collection(db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`);
    await deleteDoc(doc(productsCol, id));
};

export const subscribeToProducts = (callback: (products: Product[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const productsCol = collection(db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`);
    return onSnapshot(productsCol, (snapshot) => {
        const products: Product[] = [];
        snapshot.forEach((doc) => {
            products.push({ id: doc.id, ...(doc.data() as any) } as Product);
        });
        callback(products);
    });
};

export const updateProductsOrder = async (products: Product[]) => {
    const tenantId = getTenantId() || 'root';
    const _db = getFirestore();
    const batch = writeBatch(_db);
    products.forEach((p, index) => {
        const pRef = doc(_db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`, p.id);
        batch.update(pRef, { sortOrder: index });
    });
    await batch.commit();
};

export const updateProductGroupVisibility = async (groupId: string, isActive: boolean) => {
    const tenantId = getTenantId() || 'root';
    const _db = getFirestore();
    const batch = writeBatch(_db);
    const prodCol = collection(_db, tenantId === 'root' ? 'products' : `tenants/${tenantId}/products`);
    const q = query(prodCol);
    const snapshot = await getDocs(q);
    snapshot.docs.forEach(d => {
        const prod = d.data() as any;
        if (prod.stockGroupId === groupId) {
            batch.update(d.ref, { isActive: isActive });
        }
    });
    await batch.commit();
};

export const addIngredient = async (ingredient: Ingredient) => {
    const tenantId = getTenantId() || 'root';
    const ingredientsCol = collection(db, tenantId === 'root' ? 'ingredients' : `tenants/${tenantId}/ingredients`);
    const newDoc = doc(ingredientsCol);
    await setDoc(newDoc, cleanUndefined(ingredient));
};

export const updateIngredient = async (ingredient: Ingredient) => {
    const tenantId = getTenantId() || 'root';
    const ingredientsCol = collection(db, tenantId === 'root' ? 'ingredients' : `tenants/${tenantId}/ingredients`);
    await updateDoc(doc(ingredientsCol, ingredient.id), cleanUndefined({ ...ingredient }));
};

export const deleteIngredient = async (id: string) => {
    const tenantId = getTenantId() || 'root';
    const ingredientsCol = collection(db, tenantId === 'root' ? 'ingredients' : `tenants/${tenantId}/ingredients`);
    await deleteDoc(doc(ingredientsCol, id));
};

export const subscribeToIngredients = (callback: (ingredients: Ingredient[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const ingredientsCol = collection(db, tenantId === 'root' ? 'ingredients' : `tenants/${tenantId}/ingredients`);
    return onSnapshot(ingredientsCol, (snapshot) => {
        const ingredients: Ingredient[] = [];
        snapshot.forEach((doc) => {
            ingredients.push({ id: doc.id, ...(doc.data() as any) } as Ingredient);
        });
        callback(ingredients);
    });
};

export const saveMenu = async (menu: MenuConfig) => {
    const tenantId = getTenantId() || 'root';
    const menusCol = collection(db, tenantId === 'root' ? 'menus' : `tenants/${tenantId}/menus`);
    const menuDoc = menu.id ? doc(menusCol, menu.id) : doc(menusCol);
    if (!menu.id) menu.id = menuDoc.id;
    await setDoc(menuDoc, cleanUndefined(menu));
};

export const deleteMenu = async (id: string) => {
    const tenantId = getTenantId() || 'root';
    const menusCol = collection(db, tenantId === 'root' ? 'menus' : `tenants/${tenantId}/menus`);
    await deleteDoc(doc(menusCol, id));
};

export const subscribeToMenus = (callback: (menus: MenuConfig[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const menusCol = collection(db, tenantId === 'root' ? 'menus' : `tenants/${tenantId}/menus`);
    return onSnapshot(menusCol, (snapshot) => {
        const menus: MenuConfig[] = [];
        snapshot.forEach((doc) => {
            menus.push({ id: doc.id, ...(doc.data() as any) } as MenuConfig);
        });
        menus.sort((a, b) => (a.order || 0) - (b.order || 0));
        callback(menus);
    });
};

export const updateMenusOrder = async (menus: MenuConfig[]) => {
    const tenantId = getTenantId() || 'root';
    const _db = getFirestore();
    const batch = writeBatch(_db);
    menus.forEach((m, index) => {
        const mRef = doc(_db, tenantId === 'root' ? 'menus' : `tenants/${tenantId}/menus`, m.id);
        batch.update(mRef, { order: index });
    });
    await batch.commit();
};

export const saveCategory = async (category: CategoryConfig) => {
    const tenantId = getTenantId() || 'root';
    const categoriesCol = collection(db, tenantId === 'root' ? 'categories' : `tenants/${tenantId}/categories`);
    const catDoc = category.id ? doc(categoriesCol, category.id) : doc(categoriesCol);
    if (!category.id) category.id = catDoc.id;
    await setDoc(catDoc, cleanUndefined(category));
};

export const deleteCategory = async (id: string) => {
    const tenantId = getTenantId() || 'root';
    const categoriesCol = collection(db, tenantId === 'root' ? 'categories' : `tenants/${tenantId}/categories`);
    await deleteDoc(doc(categoriesCol, id));
};

export const subscribeToCategories = (callback: (categories: CategoryConfig[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const categoriesCol = collection(db, tenantId === 'root' ? 'categories' : `tenants/${tenantId}/categories`);
    return onSnapshot(categoriesCol, (snapshot) => {
        const categories: CategoryConfig[] = [];
        snapshot.forEach((doc) => {
            categories.push({ id: doc.id, ...(doc.data() as any) } as CategoryConfig);
        });
        categories.sort((a, b) => (a.order || 0) - (b.order || 0));
        callback(categories);
    });
};

export const updateCategoriesOrder = async (categories: CategoryConfig[]) => {
    const tenantId = getTenantId() || 'root';
    const _db = getFirestore();
    const batch = writeBatch(_db);
    categories.forEach((c, index) => {
        const cRef = doc(_db, tenantId === 'root' ? 'categories' : `tenants/${tenantId}/categories`, c.id);
        batch.update(cRef, { order: index });
    });
    await batch.commit();
};
